import { Controller } from "@hotwired/stimulus"

// Zellen innerhalb .dynamic-filter-segments (eine zusammenhängende Leiste, siehe Partial)
const DF_CELL =
  "dynamic-filter-value-wrap flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch"
const DF_CTL =
  "block w-full h-10 text-xs px-2 text-gray-800 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:ring-opacity-40 placeholder-gray-400"

// Baut beim Absenden aus Zeilen (Feld + Bedingung + Wert) die Ransack-Parameter q[attr_pred]=value.
export default class extends Controller {
  static targets = ["form", "rows", "fieldSelect", "predSelect", "valueWrap", "catalog", "catalogSearch", "rowTemplate"]
  static values = { filterable: Array, paramPrefix: { type: String, default: "q" } }

  // Ransack-Param-Namespace. Default 'q'; in eingebetteten Tabs (z. B. Anfragen
  // auf der Kundenseite) ein eigener Prefix wie 'requisitions_q', damit sich die
  // Filter mehrerer Tabs auf derselben Seite nicht ueberschreiben.
  get qp() { return this.paramPrefixValue || "q" }

  connect() {
    this._onClickOutsideCatalog = (e) => {
      if (this.hasCatalogTarget && !this.catalogTarget.closest(".dynamic-filter-catalog-wrap")?.contains(e.target)) {
        this.closeCatalog()
      }
    }
    document.addEventListener("click", this._onClickOutsideCatalog)

    this.predicatesByType = {
      string: [["enthält", "cont"], ["ist gleich", "eq"], ["beginnt mit", "start"], ["endet mit", "end"], ["ist nicht gleich", "not_eq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      integer: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      decimal: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      boolean: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      // MUSS synchron zu Helper#dynamic_filter_predicates_for('select') bleiben.
      // Fehlte bislang komplett -> select fiel auf string zurueck, daher kein "ist eine von".
      select: [["ist gleich", "eq"], ["ist eine von", "in"], ["ist keine von", "not_in"], ["ist nicht gleich", "not_eq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      date: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      datetime: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]]
    }

    // Existierende Rows hydrieren — aber nur wenn der Server-Render noch
    // nicht das richtige Control gerendert hat (sonst Flash-of-empty beim
    // Page-Load, weil JS valueSlot leert und identisch neu baut).
    const existingRows = this.hasRowsTarget
      ? this.rowsTarget.querySelectorAll(".dynamic-filter-row")
      : this.element.querySelectorAll(".dynamic-filter-row")
    existingRows.forEach((row) => {
      this.updateProgressiveUI(row)
      const slot = row.querySelector("[data-dynamic-filter-value-slot]")
      const existing = slot?.firstElementChild
      // Wenn der Server bereits ein <select> mit Optionen (Label-Auswahl)
      // gerendert hat, NICHT ueberschreiben. Sonst kurzer Flash-of-empty.
      if (existing && existing.tagName === "SELECT" && existing.options.length > 1) return
      // Durchsuchbares Tomselect (Server-gerendert) ebenfalls nicht clobbern —
      // sonst geht das aufgeloeste Label der gewaehlten Option verloren.
      if (existing && existing.matches && existing.matches("[data-controller~='tomselect-search']")) return
      this.updateValueControl(row)
    })
  }

  onFieldChange(event) {
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    this.rebuildPredicates(row, { resetPred: true })
    this.updateValueControl(row)
    this.updateProgressiveUI(row)
  }

  onPredicateChange(event) {
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    // Wert-Snapshot BEVOR updateValueControl den Input neu rendert (sonst leer).
    const previousValue = this.currentValueFromRow(row)
    this.updateValueControl(row)
    this.updateProgressiveUI(row)
    // Wert ins neue Control uebertragen, falls vorhanden.
    if (previousValue) {
      const newInput = row.querySelector("[data-dynamic-filters-target=valueInput]")
      if (newInput && newInput.tagName === "SELECT") {
        const match = Array.from(newInput.options).find((o) => o.value === previousValue)
        if (match) newInput.value = previousValue
      } else if (newInput) {
        newInput.value = previousValue
      }
    }
    this.autoSubmitIfComplete(row)
  }

  // Auto-Submit: wenn Feld + Predicate + Wert komplett (oder present/blank ohne Wert)
  autoSubmitIfComplete(row) {
    const attr = this.getAttrFromRow(row)
    if (!attr) return
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    const pred = (predSelect && predSelect.value) || ""
    if (!pred) return
    if (["present", "blank"].includes(pred)) {
      this.submitForm(new Event("auto"))
      return
    }
    const valueInput = row.querySelector("[data-dynamic-filters-target=valueInput]")
    const val = (valueInput && valueInput.value) || ""
    if (val.trim()) {
      this.submitForm(new Event("auto"))
    }
  }

  // Konvertiert Dynamic-Filter-Felder und submittet das Form
  submitAndNavigate(event) {
    if (event) event.preventDefault()
    this.submitForm(new Event("auto"))
  }

  // Debounced auto-submit bei Wert-Eingabe (nach 800ms Pause)
  debouncedAutoSubmit(event) {
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    clearTimeout(this._autoSubmitTimer)
    this._autoSubmitTimer = setTimeout(() => {
      this.autoSubmitIfComplete(row)
    }, 800)
  }

  // Multi-Select-Debounce: User muss mehrere Pools anklicken koennen, ohne
  // dass das Dropdown nach dem ersten Klick wegfliegt. Submit feuert erst,
  // wenn 1500ms keine weitere Aenderung passiert ist (oder Blur).
  debouncedMultiSubmit(event) {
    clearTimeout(this._multiSubmitTimer)
    const target = event.target
    this._multiSubmitTimer = setTimeout(() => {
      this.submitForm(new Event("auto"))
    }, 1500)
    // Sofort senden, wenn der Select den Focus verliert (User klickt woanders).
    if (!target.dataset.dfBlurBound) {
      target.dataset.dfBlurBound = "1"
      target.addEventListener("blur", () => {
        clearTimeout(this._multiSubmitTimer)
        this.submitForm(new Event("auto"))
      }, { once: true })
    }
  }

  /** Schrittweise Sichtbarkeit: Bedingung erst nach Feld, Wert erst nach Bedingung (außer present/blank). */
  updateProgressiveUI(row) {
    const attr = this.getAttrFromRow(row)
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    const pred = (predSelect && predSelect.value) || ""
    const predWrap = row.querySelector('[data-dynamic-filter-step="pred"]')
    const valueWrap = row.querySelector('[data-dynamic-filter-step="value"]')

    if (predWrap) predWrap.classList.toggle("hidden", !attr)

    const showValue = !!(attr && pred && !["present", "blank"].includes(pred))
    if (valueWrap) valueWrap.classList.toggle("hidden", !showValue)
  }

  getAttrFromRow(row) {
    const fieldSelect = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
    if (!fieldSelect) return ""
    if (fieldSelect.tomselect) {
      const v = fieldSelect.tomselect.getValue()
      return typeof v === "string" ? v : Array.isArray(v) ? v[0] || "" : ""
    }
    return fieldSelect.value || ""
  }

  conditionPlaceholder() {
    return this.formTarget?.dataset?.selectCondition || ""
  }

  rebuildPredicates(row, { resetPred = false } = {}) {
    const fieldSelect = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    if (!fieldSelect || !predSelect || !this.filterableValue.length) return

    const attr = this.getAttrFromRow(row)
    const ph = this.conditionPlaceholder()

    if (!attr) {
      predSelect.innerHTML = ""
      const o = document.createElement("option")
      o.value = ""
      o.textContent = ph
      o.selected = true
      predSelect.appendChild(o)
      return
    }

    const oldPred = resetPred ? "" : predSelect.value
    const fa = this.filterableValue.find((a) => a.ransack_attribute === attr)
    const type = (fa && fa.type) || "string"
    const preds = this.predicatesByType[type] || this.predicatesByType.string

    predSelect.innerHTML = ""
    const phOpt = document.createElement("option")
    phOpt.value = ""
    phOpt.textContent = ph
    predSelect.appendChild(phOpt)
    preds.forEach(([label, val]) => {
      const o = document.createElement("option")
      o.value = val
      o.textContent = label
      predSelect.appendChild(o)
    })

    if (resetPred) {
      predSelect.value = ""
      return
    }

    const stillValid = oldPred && preds.some(([, v]) => v === oldPred)
    predSelect.value = stillValid ? oldPred : ""
  }

  rowValIndex(fieldSelect) {
    const m = fieldSelect?.name?.match(/__attr_(\d+)/)
    return m ? m[1] : "0"
  }

  currentValueFromRow(row) {
    const el = row.querySelector("[data-dynamic-filters-target=valueInput]")
    if (!el) return ""
    if (el.tagName === "SELECT" && el.multiple) {
      return Array.from(el.selectedOptions).map((o) => o.value).filter((v) => v !== "")
    }
    if (el.tagName === "SELECT") return el.value
    return el.value || ""
  }

  updateValueControl(row) {
    const fieldSelect = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    const valueWrap = row.querySelector("[data-dynamic-filters-target=valueWrap]")
    const valueSlot = valueWrap?.querySelector("[data-dynamic-filter-value-slot]")
    if (!fieldSelect || !predSelect || !valueWrap || !valueSlot) return

    const attr = this.getAttrFromRow(row)
    const pred = predSelect.value
    const idx = this.rowValIndex(fieldSelect)
    const name = `${this.qp}[__val_${idx}]`

    const needsValue = !!(attr && pred && !["present", "blank"].includes(pred))

    const preserved = this.currentValueFromRow(row)
    valueSlot.innerHTML = ""
    if (!needsValue) {
      return
    }
    const fa = attr ? this.filterableValue.find((a) => a.ransack_attribute === attr) : null
    const type = (fa && fa.type) || "string"
    const cpOptions = fa && Array.isArray(fa.options) && fa.options.length > 0 ? fa.options : null

    // Substring-Praedikate (enthaelt / beginnt mit / endet mit) brauchen
    // ein freies Textfeld, auch wenn das Feld Kuratierte Optionen hat —
    // sonst koennte man bei 'firstname enthaelt' nicht mehr "anna" tippen.
    const substringPred = ["cont", "start", "end"].includes(pred)
    const useOptions = cpOptions && !substringPred

    const valueAria = this.formTarget?.dataset?.valueLabel || ""
    const valuePh = this.formTarget?.dataset?.valuePlaceholder || ""

    // Polymorpher Bezug (z. B. interaction.recipient_id): das Such-Model hängt
    // vom gewählten Bezug-Typ (recipient_type) in einer Schwester-Zeile ab.
    // Ist noch kein Typ gewählt, Hinweis statt totem Leer-Select.
    if (fa && fa.couple_to && fa.couple_model_map && !substringPred) {
      const siblingType = this.valueOfCatalogField(fa.couple_to)
      const coupledModel = siblingType ? fa.couple_model_map[siblingType] : null
      if (coupledModel) {
        this.appendSearchSelect(valueSlot, valueWrap, name, coupledModel, preserved, cpOptions, valueAria)
      } else {
        valueWrap.className = `${DF_CELL} w-44 min-w-32 max-w-xs sm:w-52 sm:max-w-sm`
        const hint = document.createElement("input")
        hint.type = "text"
        hint.className = `${DF_CTL} truncate`
        hint.name = name
        hint.value = Array.isArray(preserved) ? preserved[0] || "" : preserved || ""
        hint.setAttribute("data-dynamic-filters-target", "valueInput")
        const coupleField = this.filterableValue.find((f) => f.ransack_attribute === fa.couple_to)
        hint.placeholder = `Erst „${(coupleField && coupleField.label) || fa.couple_to}" wählen`
        hint.setAttribute("data-action", "keydown.enter->dynamic-filters#submitAndNavigate input->dynamic-filters#debouncedAutoSubmit")
        valueSlot.appendChild(hint)
      }
      return
    }

    // Durchsuchbares Model-Select (z. B. Kunde): tomselect-search statt fixem
    // Dropdown mit hunderten vorgeladenen Optionen. Lädt Treffer live via
    // /backend/search?model=<search_model>. Greift für Gleichheits-Prädikate.
    const searchModel = fa && fa.search_model
    if (searchModel && !substringPred) {
      // Multifilter: bei pred='in' (bzw. fa.multiple) das durchsuchbare Select
      // mehrwertig aufbauen (z. B. mehrere Kunden). Sonst einwertig wie bisher.
      const wantMultipleSearch = pred === "in" || fa?.multiple === true
      this.appendSearchSelect(valueSlot, valueWrap, name, searchModel, preserved, cpOptions, valueAria, wantMultipleSearch)
      return
    }

    let control
    if (useOptions) {
      // Multi-Select bei pred='in' ODER fa.multiple=true. Name wird zu name[],
      // damit Rails den Wert als Array annimmt. preserved kann String oder
      // Array sein (URL: ?q[pools_id_in][]=1&[]=2).
      const wantMultiple = pred === "in" || fa?.multiple === true
      valueWrap.className = `${DF_CELL} min-w-24 ${wantMultiple ? "w-48 sm:w-56" : "w-32 sm:w-36"} max-w-xs sm:max-w-sm`
      control = document.createElement("select")
      control.className = `${DF_CTL} truncate`
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = wantMultiple ? `${name}[]` : name
      if (wantMultiple) control.multiple = true
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      if (!wantMultiple) {
        const empty = document.createElement("option")
        empty.value = ""
        empty.textContent = ""
        control.appendChild(empty)
      }
      const preservedSet = Array.isArray(preserved)
        ? new Set(preserved.map(String))
        : preserved
          ? new Set([String(preserved)])
          : new Set()
      cpOptions.forEach((item) => {
        const val = typeof item === "object" && item.value !== undefined ? String(item.value) : String(item)
        const label = typeof item === "object" && item.label !== undefined ? String(item.label) : val
        const o = document.createElement("option")
        o.value = val
        o.textContent = label
        if (preservedSet.has(val)) o.selected = true
        control.appendChild(o)
      })
    } else if (type === "boolean" && ["eq", "not_eq"].includes(pred)) {
      valueWrap.className = `${DF_CELL} w-24 min-w-16 max-w-xs sm:w-28 sm:max-w-sm`
      control = document.createElement("select")
      control.className = `${DF_CTL}`
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      const yesLabel = this.formTarget?.dataset?.yesText || "true"
      const noLabel = this.formTarget?.dataset?.noText || "false"
      ;[
        ["", ""],
        [yesLabel, "true"],
        [noLabel, "false"]
      ].forEach(([label, val]) => {
        const o = document.createElement("option")
        o.value = val
        o.textContent = label
        if (
          val !== "" &&
          (val === preserved ||
            (val === "true" && (preserved === "1" || preserved === "true")) ||
            (val === "false" && (preserved === "0" || preserved === "false")))
        ) {
          o.selected = true
        }
        control.appendChild(o)
      })
    } else if (type === "date" || type === "datetime") {
      // Sowohl date- als auch datetime-Spalten (created_at/updated_at):
      // Recruiter denkt in Tagen, nie in Sekunden. Server expandiert
      // YYYY-MM-DD → Tagesbereich (merge_dynamic_filter_row_params).
      valueWrap.className = `${DF_CELL} w-44 max-w-sm sm:w-52`
      const presets = this.datePresetsForPredicate(pred)
      const container = document.createElement("div")
      container.className = "flex items-stretch gap-0"

      // Preserved kann ISO-Timestamp sein (datetime) — auf YYYY-MM-DD zuschneiden.
      const preservedDate = (preserved || "").slice(0, 10)
      // Anzeige im deutschen Format DD.MM.YYYY (Value bleibt ISO fuer Ransack).
      const fmtDe = (iso) => {
        if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || ""
        const [y, m, d] = iso.slice(0, 10).split("-")
        return `${d}.${m}.${y}`
      }

      const select = document.createElement("select")
      select.className = `${DF_CTL} truncate`
      select.name = name
      select.setAttribute("data-dynamic-filters-target", "valueInput")
      if (valueAria) select.setAttribute("aria-label", valueAria)

      const emptyOpt = document.createElement("option")
      emptyOpt.value = ""
      emptyOpt.textContent = "Datum wählen…"
      select.appendChild(emptyOpt)

      presets.forEach(({ label, value }) => {
        const o = document.createElement("option")
        o.value = value
        o.textContent = label
        if (value === preservedDate) o.selected = true
        select.appendChild(o)
      })

      // "Benutzerdefiniert" Option wenn preserved nicht in Presets
      const isCustom = preservedDate && !presets.some((p) => p.value === preservedDate)
      const customOpt = document.createElement("option")
      customOpt.value = "__custom__"
      customOpt.textContent = "📅 Benutzerdefiniert…"
      if (isCustom) {
        customOpt.value = preservedDate
        customOpt.textContent = `📅 ${fmtDe(preservedDate)}`
        customOpt.selected = true
      }
      select.appendChild(customOpt)

      select.addEventListener("change", () => {
        if (select.value === "__custom__") {
          const dateInput = document.createElement("input")
          dateInput.type = "date"
          dateInput.className = DF_CTL
          dateInput.name = name
          dateInput.setAttribute("data-dynamic-filters-target", "valueInput")
          dateInput.setAttribute("data-action", "change->dynamic-filters#submitAndNavigate")
          select.replaceWith(dateInput)
          dateInput.focus()
          dateInput.showPicker?.()
        }
      })

      container.appendChild(select)
      control = container
      // Auto-submit bei Select-Change wird unten gesetzt
      select.setAttribute("data-action", "change->dynamic-filters#submitAndNavigate")
    } else if (type === "integer") {
      valueWrap.className = `${DF_CELL} w-20 max-w-xs sm:w-24 sm:max-w-sm`
      control = document.createElement("input")
      control.type = "number"
      control.step = "1"
      // Mono + tabular-nums: Zahlen sind im Filter besser scanbar, wenn die
      // Stellen rechtsbuendig + gleich breit gerendert werden.
      control.className = `${DF_CTL} font-mono tabular-nums text-right`
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      control.value = preserved
    } else if (type === "decimal") {
      valueWrap.className = `${DF_CELL} w-20 max-w-xs sm:w-24 sm:max-w-sm`
      control = document.createElement("input")
      control.type = "text"
      control.inputMode = "decimal"
      control.className = `${DF_CTL} font-mono tabular-nums text-right`
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      control.value = preserved
      control.placeholder = valuePh
    } else {
      valueWrap.className = `${DF_CELL} w-24 min-w-16 max-w-xs sm:w-28 sm:max-w-sm`
      control = document.createElement("input")
      control.type = "text"
      control.className = DF_CTL
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      control.value = preserved
      control.placeholder = valuePh
    }

    // Auto-submit Actions je nach Control-Typ.
    // Multi-Select braucht Debounce — sonst feuert change-Event nach
    // JEDEM Pool-Klick, schliesst sich der Dropdown und der User kann
    // nicht weiter selektieren.
    if (control.tagName === "SELECT" && control.multiple) {
      control.setAttribute("data-action", "change->dynamic-filters#debouncedMultiSubmit")
    } else if (control.tagName === "SELECT" || control.type === "date" || control.type === "datetime-local") {
      control.setAttribute("data-action", "change->dynamic-filters#submitAndNavigate")
    } else if (control.tagName === "INPUT" && ["text", "number"].includes(control.type)) {
      control.setAttribute("data-action", "keydown.enter->dynamic-filters#submitAndNavigate input->dynamic-filters#debouncedAutoSubmit")
    }

    valueSlot.appendChild(control)
  }

  // Baut ein durchsuchbares tomselect-search-Select (Model-Lookup via
  // /backend/search?model=<model>) in den Value-Slot. Geteilt von search_model-
  // und couple_to-Feldern.
  appendSearchSelect(valueSlot, valueWrap, name, model, preserved, cpOptions, valueAria, multiple = false) {
    valueWrap.className = `${DF_CELL} ${multiple ? "w-52 sm:w-64" : "w-44"} min-w-32 max-w-xs sm:max-w-sm`
    const wrap = document.createElement("div")
    wrap.className = "flex min-h-10 min-w-0 flex-1 items-stretch"
    wrap.setAttribute("data-controller", "tomselect-search")
    wrap.setAttribute("data-model", model)
    if (multiple) wrap.setAttribute("data-tomselect-search-multiple-value", "true")
    const sel = document.createElement("select")
    sel.className = `${DF_CTL} truncate`
    // Multifilter: mehrwertiges Select -> name[] + multiple, TomSelect erlaubt mehrere Treffer.
    sel.name = multiple ? `${name}[]` : name
    if (multiple) sel.multiple = true
    sel.setAttribute("data-dynamic-filters-target", "valueInput")
    sel.setAttribute("data-tomselect-search-target", "input")
    if (valueAria) sel.setAttribute("aria-label", valueAria)
    if (!multiple) {
      const emptyOpt = document.createElement("option")
      emptyOpt.value = ""
      sel.appendChild(emptyOpt)
    }
    // Bei Mehrfachauswahl ALLE vorbelegten Werte rendern (URL: ?q[customer_id_in][]=1&[]=2),
    // sonst nur den ersten.
    const preservedArr = Array.isArray(preserved) ? preserved : preserved ? [preserved] : []
    const valuesToRender = multiple ? preservedArr : preservedArr.slice(0, 1)
    valuesToRender.forEach((pv) => {
      if (pv === "" || pv === null || pv === undefined) return
      const match = (cpOptions || []).find((o) => String(o.value) === String(pv))
      const o = document.createElement("option")
      o.value = String(pv)
      o.textContent = match ? String(match.label) : String(pv)
      o.selected = true
      sel.appendChild(o)
    })
    // Einwertig: sofort navigieren. Mehrwertig: Auswahl sammeln, Submit über den
    // regulaeren Filter-Submit (sonst navigiert der erste Klick zu frueh weg).
    sel.setAttribute("data-action", multiple ? "change->dynamic-filters#debouncedAutoSubmit" : "change->dynamic-filters#submitAndNavigate")
    wrap.appendChild(sel)
    valueSlot.appendChild(wrap)
  }

  // Liest den aktuell gewählten Wert eines Katalog-Felds aus der passenden
  // Filter-Zeile (für gekoppelte Felder, z. B. recipient_id ← recipient_type).
  valueOfCatalogField(attr) {
    const scope = this.hasRowsTarget ? this.rowsTarget : this.element
    const rows = scope.querySelectorAll(".dynamic-filter-row")
    for (const r of rows) {
      const fs = r.querySelector("[data-dynamic-filters-target=fieldSelect]")
      const fv = fs ? (fs.tomselect ? fs.tomselect.getValue() : fs.value) : ""
      if (fv === attr) {
        const vi = r.querySelector("[data-dynamic-filters-target=valueInput]")
        return vi ? vi.value : ""
      }
    }
    return ""
  }

  datePresetsForPredicate(pred) {
    const today = new Date()
    const fmt = (d) => d.toISOString().slice(0, 10)
    const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }
    const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
    const startOfYear = (d) => new Date(d.getFullYear(), 0, 1)
    const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)

    if (["gteq", "gt"].includes(pred)) {
      // "seit / ab" → Startdaten in der Vergangenheit
      return [
        { label: "Heute", value: fmt(today) },
        { label: "Letzte 7 Tage", value: fmt(addDays(today, -7)) },
        { label: "Letzte 30 Tage", value: fmt(addDays(today, -30)) },
        { label: "Letzte 90 Tage", value: fmt(addDays(today, -90)) },
        { label: "Seit Monatsanfang", value: fmt(startOfMonth(today)) },
        { label: "Seit Jahresanfang", value: fmt(startOfYear(today)) },
      ]
    }
    if (["lteq", "lt"].includes(pred)) {
      // "bis" → Enddaten
      return [
        { label: "Heute", value: fmt(today) },
        { label: "Gestern", value: fmt(addDays(today, -1)) },
        { label: "Monatsende", value: fmt(endOfMonth(today)) },
        { label: "Vor 7 Tagen", value: fmt(addDays(today, -7)) },
        { label: "Vor 30 Tagen", value: fmt(addDays(today, -30)) },
      ]
    }
    // eq, not_eq
    return [
      { label: "Heute", value: fmt(today) },
      { label: "Gestern", value: fmt(addDays(today, -1)) },
      { label: "Vor 7 Tagen", value: fmt(addDays(today, -7)) },
      { label: "Vor 30 Tagen", value: fmt(addDays(today, -30)) },
    ]
  }

  normalizeDateValue(val) {
    if (!val) return ""
    const s = String(val).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    const d = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
    if (d) return `${d[3]}-${d[2]}-${d[1]}`
    return s.slice(0, 10)
  }

  normalizeDatetimeLocalValue(val) {
    if (!val) return ""
    let s = String(val).trim().replace(" ", "T")
    if (s.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) return s.slice(0, 16)
    return ""
  }

  // --- Katalog-Dropdown ---

  toggleCatalog(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasCatalogTarget) return
    const isOpen = !this.catalogTarget.classList.contains("hidden")
    if (isOpen) {
      this.closeCatalog()
    } else {
      this.renderCatalog()
      this.catalogTarget.classList.remove("hidden")
      if (this.hasCatalogSearchTarget) {
        this.catalogSearchTarget.value = ""
        this.catalogSearchTarget.focus()
      }
    }
  }

  closeCatalog() {
    if (this.hasCatalogTarget) this.catalogTarget.classList.add("hidden")
  }

  filterCatalog(event) {
    const q = (event.target.value || "").toLowerCase()
    this.catalogTarget.querySelectorAll("[data-catalog-item]").forEach((item) => {
      const label = (item.dataset.catalogLabel || "").toLowerCase()
      item.classList.toggle("hidden", q.length > 0 && !label.includes(q))
    })
  }

  renderCatalog() {
    if (!this.hasCatalogTarget) return
    const list = this.catalogTarget.querySelector("[data-catalog-list]")
    if (!list) return

    // Bereits verwendete Attribute ermitteln
    const usedAttrs = new Set()
    if (this.hasRowsTarget) {
      this.rowsTarget.querySelectorAll(".dynamic-filter-row").forEach((row) => {
        const attr = this.getAttrFromRow(row)
        if (attr) usedAttrs.add(attr)
      })
    }

    list.innerHTML = ""
    // Sektions-Header
    const header = document.createElement("div")
    header.className = "px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 select-none"
    header.textContent = "Filter auswählen"
    list.appendChild(header)

    this.filterableValue.forEach((fa) => {
      const used = usedAttrs.has(fa.ransack_attribute)
      const item = document.createElement("button")
      item.type = "button"
      item.dataset.catalogItem = ""
      item.dataset.catalogLabel = fa.label
      item.dataset.catalogAttr = fa.ransack_attribute
      // Aktive Filter sind nicht mehr ausgegraut/disabled — Klick toggelt sie:
      // wenn schon aktiv → Row entfernen + Submit, sonst → Row anlegen.
      item.className = used
        ? "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-xs text-indigo-700 bg-indigo-50 hover:bg-indigo-100 cursor-pointer transition-colors"
        : "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-xs text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer transition-colors"

      const icon = fa.fa_icon || "font"
      const badge = used
        ? `<i class="fas fa-check w-3 shrink-0 text-center text-indigo-600" aria-hidden="true"></i>`
        : ""
      item.innerHTML =
        `<i class="fas fa-${icon} w-4 shrink-0 text-center text-gray-400" aria-hidden="true"></i>` +
        `<span class="flex-1 truncate">${this.escapeHtml(fa.label)}</span>` +
        badge

      item.addEventListener("click", (e) => {
        e.stopPropagation()
        e.preventDefault()
        this.closeCatalog()
        if (used) {
          this.removeRowByAttr(fa.ransack_attribute)
        } else {
          this.addRowWithField(fa.ransack_attribute)
        }
      })
      list.appendChild(item)
    })
  }

  /** Baut eine Filter-Row programmatisch (Fallback wenn kein Template im DOM). */
  buildRowElement(preselectedAttr) {
    if (!this.filterableValue.length) return null
    const idx = 0
    const ph = this.conditionPlaceholder()
    const valuePh = this.formTarget?.dataset?.valuePlaceholder || ""

    const row = document.createElement("div")
    row.className = "dynamic-filter-row inline-flex max-w-full shrink-0 flex-nowrap items-stretch"

    const segments = document.createElement("div")
    segments.className = "dynamic-filter-segments inline-flex max-w-full flex-nowrap items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm divide-x divide-gray-100 transition-shadow hover:shadow-md"

    // Feld-Select
    const fieldWrap = document.createElement("div")
    fieldWrap.className = "flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch relative w-40 min-w-24 sm:w-48"
    const fieldSelect = document.createElement("select")
    fieldSelect.name = `${this.qp}[__attr_${idx}]`
    fieldSelect.setAttribute("data-dynamic-filters-target", "fieldSelect")
    fieldSelect.setAttribute("data-action", "change->dynamic-filters#onFieldChange")
    fieldSelect.className = "block w-full h-10 text-xs px-2 text-gray-800 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:ring-opacity-40 placeholder-gray-400"
    const emptyOpt = document.createElement("option")
    emptyOpt.value = ""
    emptyOpt.textContent = this.formTarget?.dataset?.valuePlaceholder || "Feld"
    fieldSelect.appendChild(emptyOpt)
    this.filterableValue.forEach((fa) => {
      const o = document.createElement("option")
      o.value = fa.ransack_attribute
      o.textContent = fa.label
      if (preselectedAttr && fa.ransack_attribute === preselectedAttr) o.selected = true
      fieldSelect.appendChild(o)
    })
    fieldWrap.appendChild(fieldSelect)
    segments.appendChild(fieldWrap)

    // Predicate-Select
    const predWrap = document.createElement("div")
    predWrap.className = "dynamic-filter-pred-wrap shrink-0"
    predWrap.setAttribute("data-dynamic-filter-step", "pred")
    const predInner = document.createElement("div")
    predInner.className = "flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch w-28 min-w-28 sm:w-32"
    const predSelect = document.createElement("select")
    predSelect.name = `${this.qp}[__pred_${idx}]`
    predSelect.className = "block w-full h-10 text-xs px-2 text-gray-800 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:ring-opacity-40 placeholder-gray-400"
    predSelect.setAttribute("data-dynamic-filters-target", "predSelect")
    predSelect.setAttribute("data-action", "change->dynamic-filters#onPredicateChange")
    const predPh = document.createElement("option")
    predPh.value = ""
    predPh.textContent = ph
    predSelect.appendChild(predPh)
    predInner.appendChild(predSelect)
    predWrap.appendChild(predInner)
    segments.appendChild(predWrap)

    // Value
    const valWrap = document.createElement("div")
    valWrap.className = "dynamic-filter-value-wrap flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch w-24 min-w-16 max-w-xs sm:w-28 sm:max-w-sm hidden"
    valWrap.setAttribute("data-dynamic-filter-step", "value")
    valWrap.setAttribute("data-dynamic-filters-target", "valueWrap")
    const valSlot = document.createElement("div")
    valSlot.className = "flex min-h-10 min-w-0 flex-1 items-stretch"
    valSlot.setAttribute("data-dynamic-filter-value-slot", "")
    valWrap.appendChild(valSlot)
    segments.appendChild(valWrap)

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "flex min-h-10 w-8 shrink-0 items-center justify-center border-0 bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-400 focus:ring-opacity-40"
    removeBtn.setAttribute("data-action", "click->dynamic-filters#removeRow")
    removeBtn.innerHTML = '<i class="fas fa-times text-xs" aria-hidden="true"></i>'
    segments.appendChild(removeBtn)

    row.appendChild(segments)
    return row
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  addRowWithField(attr) {
    // Single-Value-Modus (siehe feat/multi-value-filters-2026-05-19): pro
    // Attribut nur eine Filter-Row. Wenn der Filter bereits existiert,
    // diese Row fokussieren statt eine zweite anzulegen. Verhindert die
    // OR-Verknüpfungs-Regression (zweite Row überschreibt den ersten
    // Wert beim Submit) und die ransack-Type-Mismatch-Fehler.
    if (attr) {
      const existing = (this.hasRowsTarget ? this.rowsTarget : document)
        .querySelectorAll(".dynamic-filter-row")
      for (const row of existing) {
        const fs = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
        const v = fs?.tomselect ? fs.tomselect.getValue() : fs?.value
        if (v === attr) {
          const valEl = row.querySelector("[data-dynamic-filters-target=valueInput]")
          if (valEl && !valEl.disabled) valEl.focus()
          return
        }
      }
    }

    // Rows-Target kann außerhalb der Controller-Element-Subtree liegen
    // (z. B. wenn Filter-Form im Header und Rows im Turbo-Frame darunter
    // rendern). Erst Stimulus-Target nutzen, sonst dokumentweit suchen,
    // erst als letzte Option neuen Container anlegen.
    // Eigenes rows-Target bevorzugen (scoped). Kein document-weiter Lookup —
    // sonst landet die Row auf einem Multi-Tab-Layout im falschen Controller.
    let rows = this.hasRowsTarget ? this.rowsTarget : null
    if (!rows) {
      const container = this.element.querySelector(".dynamic-filters-inline") || this.element
      const rowsDiv = document.createElement("div")
      rowsDiv.className = "flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-2 mt-2"
      rowsDiv.setAttribute("data-dynamic-filters-target", "rows")
      container.appendChild(rowsDiv)
      rows = rowsDiv
    }
    // Template via Stimulus-Target (scoped auf DIESEN Controller). Auf Seiten mit
    // mehreren dynamic-filters-Controllern (Multi-Tab) wuerde ein document-weiter
    // Lookup das falsche Template treffen und die Param-Namen verfaelschen.
    let rowEl
    if (this.hasRowTemplateTarget && this.rowTemplateTarget.content) {
      rowEl = document.importNode(this.rowTemplateTarget.content, true).querySelector(".dynamic-filter-row")
    } else {
      rowEl = rows.querySelector(".dynamic-filter-row")?.cloneNode(true)
    }
    if (!rowEl) {
      // Kein Template vorhanden — Row programmatisch bauen
      rowEl = this.buildRowElement(attr)
      if (!rowEl) return
    }
    const idx = rows.querySelectorAll(".dynamic-filter-row").length
    rowEl.querySelectorAll(`[name^='${this.qp}[__']`).forEach((el) => {
      el.name = el.name.replace(/\d+/, idx)
    })
    const fieldSelect = rowEl.querySelector("[data-dynamic-filters-target=fieldSelect]")
    const valInput = rowEl.querySelector("[data-dynamic-filters-target=valueInput]")
    if (valInput) valInput.value = ""
    rows.appendChild(rowEl)

    // Feld vorauswählen + ersten Predicate auto-setzen
    if (fieldSelect && attr) {
      requestAnimationFrame(() => {
        if (fieldSelect.tomselect) {
          fieldSelect.tomselect.setValue(attr, true)
        } else {
          fieldSelect.value = attr
        }
        this.rebuildPredicates(rowEl, { resetPred: true })

        // Predicate-Auto-Pick richtet sich nach effective_type des Feldes:
        //   string  → 'cont' (enthaelt)
        //   select  → 'eq'   (ist gleich) – Dropdown, Substring sinnlos
        //   date/datetime/integer/decimal → 'eq' (Tagesbereich serverseitig)
        //   boolean → 'eq'
        const predSelect = rowEl.querySelector("[data-dynamic-filters-target=predSelect]")
        if (predSelect && predSelect.options.length > 1) {
          const fa = this.filterableValue.find((a) => a.ransack_attribute === attr)
          const t = (fa && fa.type) || "string"
          const defaultPred = t === "string" || t === "text" ? "cont" : "eq"
          const match = Array.from(predSelect.options).find((o) => o.value === defaultPred)
          if (match) {
            predSelect.value = defaultPred
          } else {
            predSelect.selectedIndex = 1
          }
        }

        this.updateValueControl(rowEl)
        this.updateProgressiveUI(rowEl)

        // Value-Input fokussieren damit der User sofort tippen kann
        requestAnimationFrame(() => {
          const valInput = rowEl.querySelector("[data-dynamic-filters-target=valueInput]")
          if (valInput && !valInput.disabled) valInput.focus()
        })
      })
    } else if (fieldSelect) {
      fieldSelect.value = ""
      this.rebuildPredicates(rowEl, { resetPred: true })
      this.updateValueControl(rowEl)
      this.updateProgressiveUI(rowEl)
    }
  }

  addRow(event) {
    event.preventDefault()
    // Wenn Katalog verfügbar ist, öffne den Katalog statt direkt eine leere Row anzulegen
    if (this.hasCatalogTarget) {
      this.toggleCatalog(event)
      return
    }
    this.addRowWithField("")
  }

  disconnect() {
    if (this._onClickOutsideCatalog) {
      document.removeEventListener("click", this._onClickOutsideCatalog)
    }
  }

  removeRow(event) {
    // Rows liegen evtl. ausserhalb des Controller-Elements (Turbo-Frame); hasRowsTarget
    // ist dann false, aber die Row im DOM existiert trotzdem ueber event.target.
    // Hier KEIN hasRowsTarget-Guard — sonst klickt der User auf das X und nichts passiert.
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    row.remove()
    // Auto-submit damit der entfernte Filter auch serverseitig verschwindet (Chip + Ergebnisse)
    this.submitForm(new Event("auto"))
  }

  // Entfernt alle Rows mit dem angegebenen Feld-Attribut. Vom Katalog-Toggle
  // benutzt: Klick auf bereits aktiven Filter im Katalog → Row weg + Submit.
  removeRowByAttr(attr) {
    const scope = this.hasRowsTarget ? this.rowsTarget : this.element
    const rows = scope.querySelectorAll(".dynamic-filter-row")
    let removed = false
    rows.forEach((row) => {
      const v = row.querySelector("[data-dynamic-filters-target=fieldSelect]")?.value
      if (v === attr) { row.remove(); removed = true }
    })
    if (removed) this.submitForm(new Event("auto"))
  }

  submitForm(event) {
    const form = this.formTarget
    // Rows liegen evtl. außerhalb des Forms (Turbo-Frame-Rendering); deshalb
    // erst nach Stimulus-Target suchen, dann auf das Form fallen, zuletzt
    // dokumentweit — sonst werden bestehende Filter-Rows beim Submit eines
    // neuen Filters verschluckt (INR-6361 Multi-Filter-Regression).
    // Hat dieser Controller ein eigenes rows-Target, ist es autoritativ — KEIN
    // dokumentweiter Fallback. Sonst zieht auf einer Multi-Tab-Seite (mehrere
    // dynamic-filters-Controller) ein Tab beim Submit die Rows des anderen Tabs
    // in seinen eigenen Namespace. Nur ohne rows-Target greift der Fallback
    // (INR-6361: Rows aus Turbo-Frame-Render außerhalb des Forms).
    let rows
    if (this.hasRowsTarget) {
      rows = this.rowsTarget.querySelectorAll(".dynamic-filter-row")
    } else {
      rows = form.querySelectorAll(".dynamic-filter-row")
      if (rows.length === 0) rows = document.querySelectorAll(".dynamic-filter-row")
    }
    const keysToReplace = new Set()
    const hiddenPayload = []

    // Zuerst Werte lesen — Felder heißen q[__attr_N] / q[__pred_N] / q[__val_N] und würden
    // beim vorzeitigen remove() aus dem DOM verschwinden (querySelector in der Zeile findet sie nicht mehr).
    rows.forEach((row) => {
      const fieldEl = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
      let attr = ((fieldEl && fieldEl.value) || "").trim()
      if (!attr && fieldEl && fieldEl.tomselect) {
        const v = fieldEl.tomselect.getValue()
        attr = (typeof v === "string" ? v : Array.isArray(v) ? v[0] || "" : "").trim()
      }
      const pred = (row.querySelector("[data-dynamic-filters-target=predSelect]")?.value || "").trim()
      const valEl = row.querySelector("[data-dynamic-filters-target=valueInput]")
      let val = ""
      let valArr = null
      if (valEl) {
        if (valEl.tagName === "SELECT" && valEl.multiple) {
          valArr = Array.from(valEl.selectedOptions).map((o) => o.value).filter((v) => v !== "")
        } else {
          val = (valEl.tagName === "SELECT" ? valEl.value : valEl.value || "").trim()
        }
      }
      if (!attr || !pred) return
      keysToReplace.add(`${attr}_${pred}`)
      if (["present", "blank"].includes(pred)) {
        hiddenPayload.push({ name: `${this.qp}[${attr}_${pred}]`, value: "1" })
      } else if (["in", "not_in"].includes(pred) && valArr && valArr.length > 0) {
        // Array → mehrere hidden inputs <prefix>[attr_in][]=v1, []=v2 (bzw. attr_not_in)
        valArr.forEach((v) => hiddenPayload.push({ name: `${this.qp}[${attr}_${pred}][]`, value: v }))
      } else if (val !== "") {
        hiddenPayload.push({ name: `${this.qp}[${attr}_${pred}]`, value: val })
      }
    })

    form.querySelectorAll("input, select").forEach((el) => {
      const n = el.getAttribute("name") || ""
      if (n.startsWith(`${this.qp}[__`)) el.remove()
    })

    hiddenPayload.forEach(({ name, value }) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      form.appendChild(input)
    })
    form.querySelectorAll(`input[name^='${this.qp}['][data-dynamic-filters-keep]`).forEach((el) => {
      const match = el.name.match(new RegExp(`^${this.qp}\\[(.+)\\]$`))
      if (match && keysToReplace.has(match[1])) el.remove()
    })

    // Bei programmatischem Aufruf (auto-submit) das Form direkt absenden.
    // form.submit() statt requestSubmit() — umgeht den submit-Event-Handler
    // (sonst doppelter submitForm-Aufruf → Felder werden nochmal konvertiert → leer).
    if (event && event.type === "auto") {
      form.submit()
    }
  }
}
